// App.tsx
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { FluentProvider, webLightTheme, Button } from '@fluentui/react-components';
import { SignOutRegular } from "@fluentui/react-icons";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { Userpilot } from 'userpilot'

import { useSignInState } from './Components/signInState';
import { useHandleLogout } from './Components/HandleLogout';
import LoadingPage from './Components/LoadingPage';
import { lightPowerONTheme, darkPowerONTheme } from './Helpers/fluentPowerONTheme';
import { formatDateDDMMYYYY, formatDateISO8601, getUtcOffsetInHours } from './Helpers/dateUtils';

import AppLayout from './Layout/AppLayout';
import LandingPage from './LandingPage';
import LogoutPage from './LogoutPage';
import AcceptInvite from './AcceptInvite';
import VerifyCustomer from './VerifyCustomer';
import NotFoundPage from './NotFoundPage';

import HomeDash from './DashHome';
import { WelcomePage } from './WelcomePage';
import IntuneDashboards from './IntuneDashboards';
import CeateDashboard from './CreateDashboards';
import CustomDashboards from './CustomDashboards';
import AdminPage from './AccountAdmin';
import ProfilePage from './ProfilePage';
import PromoPage from './PromoPage';
import { AlertManagementPage } from './Components/AlertManagement';

import SignUpFlow from './SignUpFlow';
import RegisterNewCustomer from './Components/RegisterCustomer';
import UserpilotRouteTracker from './Components/UserpilotRouteTracker';
import { useConfig } from "./context/ConfigContext";

Userpilot.initialize(process.env.REACT_APP_USERPILOT_API_KEY || '');

declare let $: any;

const App = () => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const { appName } = useConfig();
  const { signInState } = useSignInState();
  const { handleLogout } = useHandleLogout();
  const [shouldRegister, setShouldRegister] = useState<boolean>(false);
  const [currentTheme, setCurrentTheme] = useState(lightPowerONTheme);
  const userInfo = signInState?.userInfo?.[0];

  useEffect(() => {
    setCurrentTheme(userInfo?.ThemePreference === 'dark' ? darkPowerONTheme : lightPowerONTheme);
    //Look at putting Reveal themeing switching here

    if (userInfo) {
      const userpilotData: any = {
        name: userInfo.DisplayName || `${userInfo.FirstName} ${userInfo.LastName}`,
        email: userInfo.Email,
        created_at: formatDateISO8601(userInfo.CreationDate), // Userpilot requires ISO8601 format for created_at field
        role: 'Admin',
        timezone: getUtcOffsetInHours(),
      };

      const customerInfo = signInState?.customerInfo?.[0];
      const licenseInfo = signInState?.licenseInfo?.[0];

      if (customerInfo) {
        userpilotData.company = {
          id: customerInfo.CustomerID,
          name: customerInfo.CompanyName,
          azureTenantId: customerInfo.TenantID,
          createdAt: formatDateDDMMYYYY(customerInfo.CreationDate),
          ...(licenseInfo && {
            onFreeTrial: licenseInfo.Type === 'Trial',
            planEnds: formatDateDDMMYYYY(licenseInfo.ExpiryDate),
            sku: licenseInfo.DisplayName,
          }), 
        };
      
      }
    
      Userpilot.identify(`${userInfo.UserID}`, userpilotData);
    }
  }, [signInState]);

  function withAdminPermissions(WrappedComponent: React.ComponentType<any>) {
    return (props: any) => {

      const hasPermission = signInState.userFeatures.some((feature: any) =>
        (feature.FeatureID === 1 || feature.FeatureID === 2) &&
        feature.Enabled === true &&
        feature.CustomerID === signInState.currentCustomerID
      );

      if (!hasPermission) {
        return <Navigate to="/" />;
      }

      return <WrappedComponent {...props} />
    };
  };
  const AdminPageWithPermissions = withAdminPermissions(AdminPage);

  function withLicense(moduleName: string, WrappedComponent: React.ComponentType<any>) {
    return (props: any) => {

      const hasPermission = signInState.licenseInfo.some(license =>
        license.CustomerID === signInState.currentCustomerID &&
        license.IsActive === true &&
        new Date(license.ExpiryDate) > new Date() &&
        license.modules.some(module => module.name.startsWith(moduleName))
      );

      if (!hasPermission) {
        return <Navigate to="/" />;
      }

      return <WrappedComponent {...props} />
    };
  };

  function withFeature(featureID: number, WrappedComponent: React.ComponentType<any>) {
    return (props: any) => {

      const hasPermission = signInState.userFeatures.some((feature: any) =>
        feature.FeatureID === featureID &&
        feature.Enabled === true &&
        feature.CustomerID === signInState.currentCustomerID
      );

      if (!hasPermission) {
        return <Navigate to="/" />;
      }

      return <WrappedComponent {...props} />
    };
  }

  useEffect(() => {
    const { location: { pathname } } = globalThis;
    if (pathname && (pathname.startsWith('/register') || pathname.startsWith('/accept-invite') || pathname.startsWith('/verify'))) {
      setCurrentTheme(lightPowerONTheme)
    }
  }, [globalThis.location.pathname, signInState]);

  useEffect(() => {
    if (!shouldRegister && !signInState.isLoading && !signInState.isAuthed && (signInState.userInfo.length === 0 || signInState.customerInfo.length === 0)) {      setShouldRegister(true);
    } else if (signInState.userInfo.length > 0 && signInState.customerInfo.length > 0) {
      setShouldRegister(false);
    }
  }, [signInState]);

  return (
    <FluentProvider theme={currentTheme}>
      <Helmet>
        <meta name="description" content={`${appName} App`} />
        <title>{appName}</title>
      </Helmet>
      <Router>
        <UserpilotRouteTracker />
        <Routes>
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/verify" element={<VerifyCustomer />} />
          <Route path="/register" element={<RegisterNewCustomer />} />
          <Route path="/*" element={
            <>
              <UnauthenticatedTemplate>
                {!isAuthenticated && (inProgress === 'logout' ? <LogoutPage /> : <LandingPage />)}
              </UnauthenticatedTemplate>
              <AuthenticatedTemplate>
                {!shouldRegister && !signInState.isAuthed && signInState.isLoading &&
                  <div className="siginwait" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100vh' }}>
                    <LoadingPage message="Checking signin details..." />
                    <div className="logoutbutton" style={{ margin: '20px' }}>
                      <Button size="medium" icon={<SignOutRegular />} onClick={handleLogout} >
                        Logout
                      </Button>
                      
                    </div>
                  </div>
                }
                {signInState.isAuthed && signInState.userInfo && signInState.customerInfo &&
                  <AppLayout>
                    <Routes>
                      <Route path="/" element={<HomeDash currentCustomerID={signInState.currentCustomerID} />} />
                      <Route path="/welcome" element={<WelcomePage />} />
                      <Route path="/intunedashboards/:tab?" element={withLicense("Reporting_Intune", IntuneDashboards)({ currentCustomerID: signInState.currentCustomerID })} />
                      <Route path="/createdash" element={withFeature(3, CeateDashboard)({ currentCustomerID: signInState.currentCustomerID })} />
                      <Route path="/customdashboards" element={withLicense("Reporting_", CustomDashboards)({ currentCustomerID: signInState.currentCustomerID })} />
                      <Route path="/alert-management" element={<AlertManagementPage />} />
                      <Route path="/admin" element={<AdminPageWithPermissions />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/promo" element={<PromoPage />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </AppLayout>
                }
                {shouldRegister && <SignUpFlow />}
              </AuthenticatedTemplate>
            </>
          } />
        </Routes>
      </Router>
    </FluentProvider>
  );
};

export default App;